/**
 * Meeting detail page
 */

import {useState, useEffect, useRef} from 'react';
import {useParams, useNavigate, useLocation} from 'react-router-dom';
import {useMeeting} from '../hooks/useMeeting';
import {useRealtimeMeeting} from '../hooks/useRealtime';
import {useMeetings} from '../hooks/useMeetings';
import {Loading} from '../components/ui/Loading';
import {Button} from '../components/ui/Button';
import {Modal} from '../components/ui/Modal';
import {Tabs, TabsList, TabsTrigger, TabsContent} from '../components/ui/Tabs';
import {MeetingForm} from '../components/meetings/MeetingForm';
import {TranscriptView} from '../components/meetings/TranscriptView';
import {SummaryView} from '../components/meetings/SummaryView';
import {ActionItemsView} from '../components/meetings/ActionItemsView';
import {AudioPlayer, type AudioPlayerRef} from '../components/audio/AudioPlayer';
import {ExportButton} from '../components/meetings/ExportButton';
import {ShareButton} from '../components/meetings/ShareButton';
import {cn} from '../utils/cn';
import {ROUTES} from '../config/constants';
import type {UpdateMeetingRequest} from '@voxapilot/shared';
import {getStorage, ref, getDownloadURL} from 'firebase/storage';
import {app} from '../config/firebase';

export function MeetingDetailPage() {
  const {id} = useParams<{id: string}>();
  const navigate = useNavigate();
  const location = useLocation();
  const meetingId = id || '';
  
  // Enable real-time updates for this meeting
  useRealtimeMeeting(meetingId);
  
  const {meeting, isLoading, error, reload} = useMeeting(meetingId);
  const {updateMeeting, deleteMeeting, isUpdating, isDeleting} = useMeetings();
  
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const [activeTab, setActiveTab] = useState('transcript');
  const audioPlayerRef = useRef<AudioPlayerRef>(null);

  // Get meeting URL for sharing
  const meetingUrl = `${window.location.origin}${location.pathname}`;

  const handleUpdateMeeting = async (data: UpdateMeetingRequest) => {
    await updateMeeting(meetingId, data);
    setIsEditModalOpen(false);
    reload();
  };

  const handleDeleteMeeting = async () => {
    await deleteMeeting(meetingId);
    setIsDeleteModalOpen(false);
    // Navigate back to meetings list after deletion
    navigate(ROUTES.MEETINGS);
  };

  // Load audio URL if available
  useEffect(() => {
    const loadAudioUrl = async () => {
      if (!meeting?.audioFile?.storagePath) {
        setAudioUrl(null);
        return;
      }

      try {
        setIsLoadingAudio(true);
        const storage = getStorage(app);
        const audioRef = ref(storage, meeting.audioFile.storagePath);
        const url = await getDownloadURL(audioRef);
        setAudioUrl(url);
      } catch (error) {
        console.error('Failed to load audio URL:', error);
        setAudioUrl(null);
      } finally {
        setIsLoadingAudio(false);
      }
    };

    if (meeting) {
      loadAudioUrl();
    }
  }, [meeting?.audioFile?.storagePath]);

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-center items-center py-12">
          <Loading size="lg" />
        </div>
      </div>
    );
  }

  if (error || !meeting) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
          <p className="text-red-800 dark:text-red-200">
            {error || 'Meeting not found'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2 break-words">
          {meeting.title}
        </h1>
        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
          <span
            className={cn(
              'px-3 py-1 text-sm font-medium rounded-full',
              {
                'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300':
                  meeting.status === 'completed',
                'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300':
                  meeting.status === 'processing',
                'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300':
                  meeting.status === 'recording',
                'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300':
                  meeting.status === 'draft',
              }
            )}
          >
            {meeting.status}
          </span>
          {meeting.scheduledDate && (
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {new Date(meeting.scheduledDate).toLocaleDateString()}
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        <div className="lg:col-span-2 space-y-4 sm:space-y-6 order-2 lg:order-1">
          {meeting.notes && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Notes
              </h2>
              <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                {meeting.notes}
              </p>
            </div>
          )}

          {/* Audio Player */}
          {audioUrl && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Audio Recording
              </h2>
              {isLoadingAudio ? (
                <div className="flex justify-center py-8">
                  <Loading />
                </div>
              ) : (
                <AudioPlayer ref={audioPlayerRef} src={audioUrl} title={meeting.title} />
              )}
            </div>
          )}

          {/* Tabs for Transcript, Summary, and Action Items */}
          {(meeting.transcriptionStatus === 'completed' ||
            meeting.summarizationStatus === 'completed') && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <Tabs value={activeTab} onValueChange={setActiveTab} defaultValue="transcript">
                <TabsList>
                  {meeting.transcriptionStatus === 'completed' && (
                    <TabsTrigger value="transcript">Transcript</TabsTrigger>
                  )}
                  {meeting.summarizationStatus === 'completed' && (
                    <>
                      <TabsTrigger value="summary">Summary</TabsTrigger>
                      <TabsTrigger value="action-items">Action Items</TabsTrigger>
                    </>
                  )}
                </TabsList>

                {meeting.transcriptionStatus === 'completed' && (
                  <TabsContent value="transcript">
                    <TranscriptView meetingId={meeting.id} audioPlayerRef={audioPlayerRef} />
                  </TabsContent>
                )}

                {meeting.summarizationStatus === 'completed' && (
                  <>
                    <TabsContent value="summary">
                      <SummaryView meetingId={meeting.id} />
                    </TabsContent>
                    <TabsContent value="action-items">
                      <ActionItemsView meetingId={meeting.id} />
                    </TabsContent>
                  </>
                )}
              </Tabs>
            </div>
          )}
        </div>

        <div className="space-y-4 sm:space-y-6 order-1 lg:order-2">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Details
            </h2>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Created
                </dt>
                <dd className="text-sm text-gray-900 dark:text-white">
                  {new Date(meeting.createdAt).toLocaleString()}
                </dd>
              </div>
              {meeting.participants && meeting.participants.length > 0 && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Participants
                  </dt>
                  <dd className="text-sm text-gray-900 dark:text-white">
                    {meeting.participants.join(', ')}
                  </dd>
                </div>
              )}
              {meeting.location && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Location
                  </dt>
                  <dd className="text-sm text-gray-900 dark:text-white">
                    {meeting.location}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Actions
            </h2>
            <div className="space-y-2">
              <ShareButton
                meetingTitle={meeting.title}
                meetingUrl={meetingUrl}
              />
              <ExportButton
                meetingId={meeting.id}
                meetingTitle={meeting.title}
              />
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setIsEditModalOpen(true)}
              >
                Edit Meeting
              </Button>
              <Button
                variant="danger"
                className="w-full"
                onClick={() => setIsDeleteModalOpen(true)}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete Meeting'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <Modal
        open={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        title="Edit Meeting"
        size="lg"
      >
        <MeetingForm
          initialData={meeting}
          onSubmit={handleUpdateMeeting}
          onCancel={() => setIsEditModalOpen(false)}
          isLoading={isUpdating}
          isEdit={true}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={isDeleteModalOpen}
        onOpenChange={setIsDeleteModalOpen}
        title="Delete Meeting"
        description="Are you sure you want to delete this meeting? This action cannot be undone."
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            This will permanently delete the meeting and all associated data
            (transcripts, summaries, audio files).
          </p>
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setIsDeleteModalOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteMeeting}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

